/**
 * UnifiedCategorySelector V2
 * ===========================
 * A single category selector that works across all FinList modules
 * using the unified category system.
 * 
 * @version 2.0.0
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, ChevronDown, ChevronRight, X, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';

// Import unified categories
import {
  INCOME_CATEGORIES,
  EXPENSE_CATEGORIES,
  BUDGET_ENVELOPES,
  SAVINGS_GOAL_CATEGORIES,
  FINTASK_CATEGORIES,
  SHOPPING_LIST_CATEGORIES,
  getCategoriesByType,
  findCategoryById,
  getSubcategories,
  getExpenseCategoriesForContext
} from '@/constants/unifiedCategories';

/**
 * Category type constants
 */
export const CATEGORY_MODULES = {
  INCOME: 'income',
  EXPENSE: 'expense',
  BUDGET: 'budget',
  SAVINGS: 'savings',
  GOALS: 'goals',
  TASKS: 'tasks',
  FINTASKS: 'fintasks',
  SHOPPING: 'shopping',
  TRANSACTIONS: 'transactions',
  TRANSACTIONS_INCOME: 'transactions-income'
};

/**
 * Get categories for a specific module
 * FINLIST CONTEXT: Now supports context filtering for expense categories
 * @param {string} module - The module type
 * @param {string} context - Optional context ('expense' or 'shopping') for filtering
 */
function getCategoriesForModule(module, context = null) {
  switch (module) {
    case CATEGORY_MODULES.INCOME:
    case CATEGORY_MODULES.TRANSACTIONS_INCOME:
    case 'transactions-income':
    case 'income':
      return INCOME_CATEGORIES;
    case CATEGORY_MODULES.EXPENSE:
    case CATEGORY_MODULES.TRANSACTIONS:
      // FINLIST CONTEXT: Filter expense categories by context
      // 'expense' context hides Groceries & Personal Care (use Shopping List instead)
      return context ? getExpenseCategoriesForContext(context) : EXPENSE_CATEGORIES;
    case CATEGORY_MODULES.BUDGET:
      return BUDGET_ENVELOPES;
    case CATEGORY_MODULES.SAVINGS:
    case CATEGORY_MODULES.GOALS:
      return SAVINGS_GOAL_CATEGORIES;
    case CATEGORY_MODULES.TASKS:
    case CATEGORY_MODULES.FINTASKS:
      return FINTASK_CATEGORIES;
    case CATEGORY_MODULES.SHOPPING:
      // FINLIST CONTEXT: Shopping module uses full expense categories for Groceries & Personal Care
      return getExpenseCategoriesForContext('shopping');
    default:
      return context ? getExpenseCategoriesForContext(context) : EXPENSE_CATEGORIES;
  }
}

/**
 * Render icon component from lucide-react
 */
function CategoryIcon({ icon, emoji, color, size = 18 }) {
  // If emoji is provided and icon is not a component, use emoji
  if (emoji && typeof icon === 'string') {
    return (
      <span className="text-lg" style={{ fontSize: size + 2 }}>
        {emoji}
      </span>
    );
  }

  // If icon is a React component (from lucide-react)
  if (icon && typeof icon !== 'string') {
    const IconComponent = icon;
    return <IconComponent size={size} style={{ color }} />;
  }

  // If icon is a string name from lucide-react
  if (typeof icon === 'string' && LucideIcons[icon]) {
    const IconComponent = LucideIcons[icon];
    return <IconComponent size={size} style={{ color }} />;
  }

  // Fallback to emoji or default icon
  if (emoji) {
    return <span className="text-lg">{emoji}</span>;
  }

  return <LucideIcons.Tag size={size} style={{ color: color || '#6b7280' }} />;
}

/**
 * Main UnifiedCategorySelector Component
 * 
 * Props:
 * - module: 'income' | 'expense' | 'budget' | 'savings' | 'goals' | 'tasks' | 'fintasks' | 'shopping' | 'transactions'
 * - context: 'expense' | 'shopping' - FINLIST CONTEXT: Controls which categories are shown
 *   - 'expense': Hides Groceries & Personal Care (use Shopping List instead)
 *   - 'shopping': Shows Groceries & Personal Care (canonical expense categories)
 * - value: { categoryId, subcategoryId?, customCategory?, customSubcategory? }
 * - onChange: (value) => void
 * - showSubcategories: boolean (default true)
 * - allowCustom: boolean (default true)
 * - required: boolean
 * - placeholder: string
 * - className: string
 * - compact: boolean (for inline/smaller displays)
 */
export function UnifiedCategorySelector({
  module = CATEGORY_MODULES.EXPENSE,
  context = null, // FINLIST CONTEXT: 'expense' or 'shopping'
  value: propValue,
  onChange,
  showSubcategories = true,
  allowCustom = true,
  required = false,
  placeholder = "Select category...",
  className = "",
  compact = false
}) {
  // Ensure value is always a valid object with defaults
  const value = propValue ?? { categoryId: null, subcategoryId: null };
  const safeValue = {
    categoryId: value?.categoryId ?? null,
    subcategoryId: value?.subcategoryId ?? null,
    customCategory: value?.customCategory ?? null,
    customSubcategory: value?.customSubcategory ?? null
  };

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Get categories for the module, filtered by context
  // FINLIST CONTEXT: Pass context to filter Groceries & Personal Care appropriately
  const categories = useMemo(() => getCategoriesForModule(module, context), [module, context]);

  // Filter categories based on search
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    
    const query = searchQuery.toLowerCase();
    return categories.filter(cat => {
      const nameMatch = cat.name.toLowerCase().includes(query);
      const descMatch = cat.description?.toLowerCase().includes(query);
      const subMatch = cat.subcategories?.some(sub => 
        sub.name.toLowerCase().includes(query)
      );
      return nameMatch || descMatch || subMatch;
    });
  }, [categories, searchQuery]);

  // Get selected category display
  const selectedCategory = useMemo(() => {
    if (!safeValue.categoryId) return null;
    if (safeValue.categoryId === '__custom__') {
      return { name: safeValue.customCategory || 'Custom', emoji: '✏️', color: '#6b7280' };
    }
    return categories.find(cat => cat.id === safeValue.categoryId);
  }, [categories, safeValue]);

  const selectedSubcategory = useMemo(() => {
    if (!selectedCategory || !safeValue.subcategoryId) return null;
    return selectedCategory.subcategories?.find(sub => sub.id === safeValue.subcategoryId);
  }, [selectedCategory, safeValue.subcategoryId]);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  function handleCategorySelect(category) {
    if (showSubcategories && category.subcategories?.length > 0) {
      // Toggle expanded state
      setExpandedCategory(expandedCategory === category.id ? null : category.id);
    } else {
      // Select category directly
      onChange({
        categoryId: category.id,
        subcategoryId: null,
        customCategory: null,
        customSubcategory: null
      });
      setIsOpen(false);
      setSearchQuery('');
    }
  }

  function handleSubcategorySelect(category, subcategory) {
    onChange({
      categoryId: category.id,
      subcategoryId: subcategory.id,
      customCategory: null,
      customSubcategory: null
    });
    setIsOpen(false);
    setSearchQuery('');
  }

  function handleCategoryOnlySelect(category) {
    onChange({
      categoryId: category.id,
      subcategoryId: null,
      customCategory: null,
      customSubcategory: null
    });
    setIsOpen(false);
    setSearchQuery('');
  }

  function handleCustomSelect() {
    onChange({
      categoryId: '__custom__',
      subcategoryId: null,
      customCategory: '',
      customSubcategory: ''
    });
    setIsOpen(false);
    setSearchQuery('');
  }

  // Render selected display
  const renderSelectedDisplay = () => {
    if (!selectedCategory) {
      return <span className="text-gray-400">{placeholder}</span>;
    }

    return (
      <div className="flex items-center gap-2">
        <div 
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${selectedCategory.color}20` }}
        >
          <CategoryIcon 
            icon={selectedCategory.icon}
            emoji={selectedCategory.emoji}
            color={selectedCategory.color}
            size={16}
          />
        </div>
        <div className="flex-1 min-w-0">
          <span className="font-medium text-gray-900 truncate">
            {selectedCategory.name}
            {selectedSubcategory && (
              <span className="text-gray-500 font-normal"> › {selectedSubcategory.name}</span>
            )}
          </span>
        </div>
      </div>
    );
  };

  // Render category item
  const renderCategoryItem = (category) => {
    const isExpanded = expandedCategory === category.id;
    const isSelected = safeValue.categoryId === category.id;
    const hasSubcategories = showSubcategories && category.subcategories?.length > 0;

    return (
      <div key={category.id}>
        <button
          type="button"
          onClick={() => handleCategorySelect(category)}
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left
            hover:bg-gray-50 transition-all duration-150
            ${isSelected && !safeValue.subcategoryId ? 'bg-[#006D77]/10 ring-2 ring-[#006D77]' : ''}
          `}
        >
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${category.color}20` }}
          >
            <CategoryIcon 
              icon={category.icon}
              emoji={category.emoji}
              color={category.color}
              size={18}
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900 truncate">
              {category.name}
            </div>
            {category.description && !compact && (
              <div className="text-xs text-gray-500 truncate">
                {category.description}
              </div>
            )}
          </div>

          {hasSubcategories && (
            <motion.div
              animate={{ rotate: isExpanded ? 90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronRight size={16} className="text-gray-400" />
            </motion.div>
          )}
        </button>

        {/* Subcategories */}
        <AnimatePresence>
          {hasSubcategories && isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="ml-6 pl-5 border-l-2 border-gray-200 py-1 space-y-0.5">
                {/* Option to select just the parent category */}
                <button
                  type="button"
                  onClick={() => handleCategoryOnlySelect(category)}
                  className={`
                    w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm
                    hover:bg-gray-50 transition-colors
                    ${isSelected && !safeValue.subcategoryId ? 'bg-[#006D77]/10 text-[#006D77] font-semibold' : 'text-gray-600'}
                  `}
                >
                  <span className="w-2 h-2 rounded-full bg-gray-300" />
                  <span>All {category.name}</span>
                </button>

                {/* Subcategory options */}
                {category.subcategories.map(sub => (
                  <button
                    key={sub.id}
                    type="button"
                    onClick={() => handleSubcategorySelect(category, sub)}
                    className={`
                      w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm
                      hover:bg-gray-50 transition-colors
                      ${safeValue.subcategoryId === sub.id ? 'bg-[#006D77]/10 text-[#006D77] font-semibold' : 'text-gray-700'}
                    `}
                  >
                    <span 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: safeValue.subcategoryId === sub.id ? '#006D77' : '#d1d5db' }}
                    />
                    <span>{sub.name}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between gap-2 
          ${compact ? 'px-3 py-2' : 'px-4 py-3'} 
          bg-white border-2 border-gray-200 rounded-xl 
          hover:border-[#006D77] focus:outline-none focus:ring-2 focus:ring-[#006D77] focus:border-transparent 
          transition-all duration-200
        `}
      >
        {renderSelectedDisplay()}
        
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown size={18} className="text-gray-400" />
        </motion.div>
      </button>

      {/* Custom category inputs (when __custom__ is selected) */}
      {safeValue.categoryId === '__custom__' && (
        <div className="mt-2 space-y-2">
          <input
            type="text"
            placeholder="Enter custom category name"
            value={safeValue.customCategory || ''}
            onChange={(e) => onChange({ ...safeValue, customCategory: e.target.value })}
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#006D77] focus:border-transparent"
            required={required}
          />
          <input
            type="text"
            placeholder="Custom subcategory (optional)"
            value={safeValue.customSubcategory || ''}
            onChange={(e) => onChange({ ...safeValue, customSubcategory: e.target.value })}
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#006D77] focus:border-transparent"
          />
        </div>
      )}

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-[450px] overflow-hidden"
          >
            {/* Search Bar */}
            <div className="sticky top-0 bg-white border-b border-gray-100 p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search categories..."
                  className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#006D77] focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Categories List */}
            <div className="overflow-y-auto max-h-[350px] p-2">
              {filteredCategories.length > 0 ? (
                <div className="space-y-0.5">
                  {filteredCategories.map(cat => renderCategoryItem(cat))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No categories found</p>
                </div>
              )}

              {/* Custom Category Option */}
              {allowCustom && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleCustomSelect}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Sparkles size={16} className="text-gray-500" />
                    </div>
                    <span className="text-gray-600 font-medium">Custom Category...</span>
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Simple Category Badge (for display only)
 */
export function CategoryBadge({ categoryId, subcategoryId, showSubcategory = true, className = "" }) {
  const category = findCategoryById(categoryId);
  
  if (!category) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-sm ${className}`}>
        <span>📌</span>
        <span>{categoryId || 'Uncategorized'}</span>
      </span>
    );
  }

  const subcategory = subcategoryId ? getSubcategories(categoryId).find(s => s.id === subcategoryId) : null;

  return (
    <span 
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-medium ${className}`}
      style={{ 
        backgroundColor: `${category.color}15`,
        color: category.color
      }}
    >
      <span>{category.emoji}</span>
      <span>{category.name}</span>
      {showSubcategory && subcategory && (
        <span className="text-xs opacity-75">› {subcategory.name}</span>
      )}
    </span>
  );
}

/**
 * Quick Category Pills (for quick selection)
 */
export function QuickCategoryPills({ 
  module = CATEGORY_MODULES.EXPENSE, 
  selected, 
  onSelect, 
  limit = 6,
  className = ""
}) {
  const categories = useMemo(() => getCategoriesForModule(module).slice(0, limit), [module, limit]);

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {categories.map(cat => (
        <button
          key={cat.id}
          type="button"
          onClick={() => onSelect(cat)}
          className={`
            inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
            transition-all duration-150
            ${selected?.id === cat.id 
              ? 'ring-2 ring-offset-1' 
              : 'hover:opacity-80'
            }
          `}
          style={{ 
            backgroundColor: `${cat.color}20`,
            color: cat.color,
            ...(selected?.id === cat.id ? { ringColor: cat.color } : {})
          }}
        >
          <span>{cat.emoji}</span>
          <span>{cat.name}</span>
        </button>
      ))}
    </div>
  );
}

// Default export
export default UnifiedCategorySelector;
